import { auth } from "../config/firebase";

// Para todas las peticiones a la API, usar apiCall() o fetchAuthRetry() del api.service.js

// Normalizar el API_URL para evitar doble /api/
const API_URL = (() => {
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  console.log(`API URL base: ${baseUrl}`);
  
  // Si estamos en producción y la URL base es /api, necesitamos manejar el caso especial
  // donde los endpoints podrían estar en /backend
  if (baseUrl === '/api' && window.location.hostname === 'bt4data.bellatwin.com') {
    console.log('Detectado entorno de producción con URL relativa. Usando /backend como base.');
    return '/backend';
  }
  
  return baseUrl;
})();

// API_URL se mantiene para construir URLs especiales fuera de /api (login, logout)

const AuthService = {
  /**
   * Get the current auth status from the server
   * @returns {Promise<Object>} User data or null
   */
  getAuthStatus: async () => {
    try {
      // Intentar obtener el estado desde el endpoint de autenticación
      console.log("Obteniendo estado de autenticación...");
      
      try {
        // Usar apiCall para obtener el estado de autenticación
        const { apiCall } = await import('./api.service');
        const userData = await apiCall('auth/status', 'GET', null, false, { forceWaitAuth: true });
        console.log("Respuesta del servidor:", userData);
        
        // Solo si está autenticado, almacenamos en localStorage para referencia
        if (userData && userData.authenticated) {
          localStorage.setItem('user', JSON.stringify(userData));
          return userData;
        } else {
          console.log("El servidor indica que no hay autenticación válida");
          localStorage.removeItem('user');
          return null;
        }
      } catch (apiError) {
        // Solo registrar advertencias para errores que no sean silenciosos
        if (!apiError.message.includes('failed silently')) {
          console.warn(`Error al obtener estado de autenticación: ${apiError.message}`);
        }
        
        // Como fallback, intentamos obtener datos del localStorage
        // OBSOLETE: This function reads from localStorage
        console.warn('[AuthService] OBSOLETE: Reading from localStorage. Use useAuth() hook or UserSessionRegistry instead.');
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            console.log("Usuario recuperado del localStorage (fallback):", userData);
            return userData;
          } catch (parseError) {
            console.error("Error al parsear usuario de localStorage:", parseError);
            localStorage.removeItem('user');
          }
        }
      }
      
      return null;
    } catch (error) {
      // Solo registrar errores para errores que no sean silenciosos
      if (!error.message.includes('failed silently')) {
        console.error('Error al obtener estado de autenticación:', error);
      }
      
      // Como fallback en caso de error, intentar localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          return JSON.parse(storedUser);
        } catch (parseError) {
          localStorage.removeItem('user');
        }
      }
      
      return null;
    }
  },
  
  /**
   * Perform a login with the fake login endpoint
   * @param {Object} loginData Login credentials
   * @returns {Promise<Object>} Login response
   */
  login: async (loginData) => {
    try {
      console.log("Iniciando login con datos:", loginData);
      
      // Crear FormData para enviar al endpoint de login
      const formData = new FormData();
      formData.append('email', loginData.email);
      formData.append('name', loginData.name);
      formData.append('organization_id', loginData.organizationId);
      formData.append('role_id', loginData.roleId);
      
      // El login es un caso especial, va fuera de /api
      // Para /backend, usar /backend/auth/login
      // Para otros, usar /auth/login directamente
      const loginUrl = API_URL === '/backend' 
        ? '/backend/auth/login' 
        : `${API_URL}/auth/login`;
        
      console.log(`URL de login: ${loginUrl}`);
      
      // Llamar al endpoint real de login
      await fetch(loginUrl, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        redirect: 'manual' // No seguir redirecciones
      });
      
      // Verificar estado de autenticación para obtener los datos del usuario
      const userData = await AuthService.getAuthStatus();
      
      // En desarrollo, si no hay datos de usuario, crear un usuario simulado
      // para permitir el flujo de trabajo sin backend completo
      if (!userData && import.meta.env.MODE === 'development') {
        console.log("Usando login simulado en desarrollo porque no hay backend disponible");
        
        // Crear usuario simulado con los datos del formulario
        const mockUser = {
          authenticated: true,
          email: loginData.email,
          name: loginData.name,
          organization_id: parseInt(loginData.organizationId),
          organization_name: "Organización " + loginData.organizationId,
          role_id: parseInt(loginData.roleId),
          role_code: loginData.roleId == 1 ? "super_user" : (loginData.roleId == 2 ? "admin" : "user"),
          role_name: ["SuperUsuario", "Administrador", "Usuario"][parseInt(loginData.roleId) - 1] || "Usuario",
          user_id: 1,
          is_impersonated: false
        };
        
        // Guardar en localStorage
        localStorage.setItem('user', JSON.stringify(mockUser));
        return mockUser;
      }
      
      if (!userData) {
        throw new Error('No se pudo obtener los datos del usuario después del login');
      }
      
      console.log("Login exitoso:", userData);
      return userData;
    } catch (error) {
      // En modo desarrollo, permitir login incluso con errores
      if (import.meta.env.MODE === 'development' && 
          (error.message.includes('No se pudo obtener los datos del usuario') || 
           error.message.includes('failed silently'))) {
        
        console.log("Usando login simulado en desarrollo debido a error:", error.message);
        
        // Crear usuario simulado con los datos del formulario
        const mockUser = {
          authenticated: true,
          email: loginData.email,
          name: loginData.name,
          organization_id: parseInt(loginData.organizationId),
          organization_name: "Organización " + loginData.organizationId,
          role_id: parseInt(loginData.roleId),
          role_code: loginData.roleId == 1 ? "super_user" : (loginData.roleId == 2 ? "admin" : "user"),
          role_name: ["SuperUsuario", "Administrador", "Usuario"][parseInt(loginData.roleId) - 1] || "Usuario",
          user_id: 1,
          is_impersonated: false
        };
        
        // Guardar en localStorage
        localStorage.setItem('user', JSON.stringify(mockUser));
        return mockUser;
      }
      
      console.error('Login error:', error);
      throw error;
    }
  },
  
  /**
   * Logout the current user
   * @returns {Promise<void>}
   */
  logout: async () => {
    try {
      console.log("Cerrando sesión...");
      
      // Intentar hacer logout en el servidor
      try {
        // El logout es un caso especial, va fuera de /api
        // Para /backend, usar /backend/auth/logout
        // Para otros, usar /auth/logout directamente
        const logoutUrl = API_URL === '/backend' 
          ? '/backend/auth/logout' 
          : `${API_URL}/auth/logout`;
          
        console.log(`URL de logout: ${logoutUrl}`);
        // Logout es endpoint público fuera de /api, usar fetch directo
        await fetch(logoutUrl, {
          method: 'POST',
          credentials: 'include'
        });
        console.log("Sesión cerrada en el servidor correctamente");
      } catch (serverError) {
        console.warn("Error al intentar cerrar sesión en el servidor:", serverError.message);
      }
      
      // Siempre limpiar datos locales
      localStorage.removeItem('user');
      localStorage.removeItem('originalUser');
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },
  
  /**
   * Start impersonation of a user
   * @param {string} email Email of the user to impersonate
   * @param {string} reason Reason for impersonation
   * @returns {Promise<Object>} Impersonation result
   */
  startImpersonation: async (email, reason = '') => {
    try {
      // Intentar usar el endpoint real
      try {
        // Usar apiCall para impersonación
        const { apiCall } = await import('./api.service');
        await apiCall(`users/${email}/impersonate`, 'POST', { reason });
        console.log(`Impersonación de ${email} iniciada correctamente`);
      } catch (apiError) {
        console.warn(`Usando impersonación simulada: ${apiError.message}`);
        // Simular impersonación como fallback
        const originalUser = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('originalUser', JSON.stringify(originalUser));
        
        const impersonatedUser = {
          ...originalUser,
          email: email,
          name: email.split('@')[0],
          is_impersonated: true
        };
        
        localStorage.setItem('user', JSON.stringify(impersonatedUser));
      }
      
      return { success: true };
    } catch (error) {
      console.error('Impersonation error:', error);
      throw error;
    }
  },
  
  /**
   * End the current impersonation session
   * @returns {Promise<Object>} End impersonation result
   */
  endImpersonation: async () => {
    try {
      // Intentar usar el endpoint real
      try {
        // Usar apiCall para finalizar impersonación
        const { apiCall } = await import('./api.service');
        await apiCall('users/end_impersonation', 'POST');
        console.log("Impersonación finalizada correctamente");
      } catch (apiError) {
        console.warn(`Usando finalización de impersonación simulada: ${apiError.message}`);
        // Simulación como fallback
        const originalUser = JSON.parse(localStorage.getItem('originalUser') || '{}');
        localStorage.setItem('user', JSON.stringify(originalUser));
        localStorage.removeItem('originalUser');
      }
      
      return { success: true };
    } catch (error) {
      console.error('End impersonation error:', error);
      throw error;
    }
  },

  /**
   * Get current user profile information
   * @returns {Object|null} User profile or null
   */
  getCurrentUser: () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        return JSON.parse(storedUser);
      }
      return null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  /**
   * Get all available organizations for login
   * @returns {Promise<Array>} List of organizations
   */
  getOrganizations: async () => {
    try {
      // Importar la función apiCall para asegurar consistencia en las llamadas a la API
      const { apiCall } = await import('./api.service');
      
      try {
        console.log("Obteniendo organizaciones mediante apiCall con autenticación forzada");
        
        // Usar apiCall con espera explícita de autenticación para asegurar headers completos
        const organizations = await apiCall('organizations', 'GET', null, false, { forceWaitAuth: true });
        
        if (organizations && Array.isArray(organizations) && organizations.length > 0) {
          console.log(`Obtenidas ${organizations.length} organizaciones correctamente`);
          return organizations;
        }
        
        // Si organizations no es un array válido o está vacío
        if (!Array.isArray(organizations)) {
          console.error("La respuesta del servidor no es un array:", organizations);
          return [];
        }
        
        return organizations; // Devolver array vacío si no hay organizaciones
      } catch (apiError) {
        // Si el error es de autenticación (401, 403), registrarlo claramente
        if (apiError.message && (apiError.message.includes('401') || apiError.message.includes('403'))) {
          console.error("Error de autenticación al obtener organizaciones. Verifica que estás autenticado correctamente.", apiError);
        } else {
          console.error("Error obteniendo organizaciones desde API:", apiError);
        }
        
        // Intentar una vez más con autenticación forzada y timeout extendido
        try {
          console.log("Reintentando obtener organizaciones con timeout extendido...");
          const organizations = await apiCall('organizations', 'GET', null, true, { forceWaitAuth: true });
          
          if (Array.isArray(organizations)) {
            console.log("Organizaciones obtenidas en segundo intento:", organizations);
            return organizations;
          }
        } catch (retryError) {
          console.error("Error en segundo intento de obtener organizaciones:", retryError);
        }
        
        // Si todo falla, devolver array vacío
        return [];
      }
    } catch (error) {
      console.error('Error general obteniendo organizaciones:', error);
      return [];
    }
  },

  /**
   * Get all available roles for login
   * @returns {Promise<Array>} List of roles
   */
  getRoles: async () => {
    try {
      // Importar el servicio de roles
      const RoleService = (await import('./role.service')).default;
      
      // Usar el servicio específico de roles para obtener los roles
      return await RoleService.getRoles();
    } catch (error) {
      console.error('Error obteniendo roles:', error);
      // Devolver roles predeterminados como último recurso
      return [
        { id: 1, code: "super_user", name: "SuperUsuario" },
        { id: 2, code: "admin", name: "Administrador" },
        { id: 3, code: "user", name: "Usuario" }
      ];
    }
  },

  /**
   * Valida si el usuario actual tiene acceso a un módulo específico en una organización específica
   * @param {string} moduleCode - Código del módulo a validar
   * @param {number} [organizationId] - ID de la organización (opcional)
   * @returns {Promise<boolean>} - True si tiene acceso, false en caso contrario
   */
  validateModuleAccess: async (moduleCode, organizationId = null) => {
    try {
      const { apiCall } = await import('./api.service');
      const params = { module_code: moduleCode };
      if (organizationId) {
        params.organization_id = organizationId;
      }
      
      const result = await apiCall('auth/validate-module-access', 'GET', null, false, { 
        params 
      });
      
      return result?.has_access || false;
    } catch (error) {
      console.error('Error validating module access:', error);
      return false;
    }
  },

  /**
   * Obtiene los módulos disponibles para el usuario en una organización específica
   * @param {number} organizationId - ID de la organización
   * @returns {Promise<Array>} - Lista de módulos disponibles
   */
  getModulesForOrganization: async (organizationId) => {
    try {
      const { apiCall } = await import('./api.service');
      return await apiCall('users/current/modules', 'GET', null, false, { 
        params: { organization_id: organizationId } 
      });
    } catch (error) {
      console.error('Error fetching modules for organization:', error);
      return [];
    }
  },

  /**
   * Cambia el contexto de organización activa para el usuario
   * @param {number} organizationId - ID de la nueva organización activa
   * @returns {Promise<Object>} - Resultado del cambio de contexto
   */
  switchOrganizationContext: async (organizationId) => {
    try {
      // Usar fetchAuthRetry para manejar reintentos automáticos de autenticación
      const { fetchAuthRetry } = await import('./api.service');
      return await fetchAuthRetry('auth/switch-organization', 'POST', { organization_id: organizationId }, {
        forceWaitAuth: true
      });
    } catch (error) {
      console.error('Error switching organization context:', error);
      throw error;
    }
  },
};

export default AuthService; 